/**
 * DimensionTagNode - Custom Lexical node for dimension tags.
 *
 * Renders inline tags like [DIMENSION:123:Label] in the editor
 * and converts to/from markdown format.
 */

import React, { ReactNode } from "react";
import {
  DecoratorNode,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from "lexical";

// ============================================
// Serialized Type
// ============================================

export type SerializedDimensionTagNode = Spread<
  {
    dimId: number;
    label: string;
    group: string;
  },
  SerializedLexicalNode
>;

// ============================================
// Node Class
// ============================================

export class DimensionTagNode extends DecoratorNode<ReactNode> {
  __dimId: number;
  __label: string;
  __group: string;

  static getType(): string {
    return "dimension-tag";
  }

  static clone(node: DimensionTagNode): DimensionTagNode {
    return new DimensionTagNode(
      node.__dimId,
      node.__label,
      node.__group,
      node.__key
    );
  }

  constructor(
    dimId: number,
    label: string = "",
    group: string = "",
    key?: NodeKey
  ) {
    super(key);
    this.__dimId = dimId;
    this.__label = label;
    this.__group = group;
  }

  // Getters
  getDimId(): number {
    return this.__dimId;
  }

  getLabel(): string {
    return this.__label;
  }

  getGroup(): string {
    return this.__group;
  }

  // DOM Creation
  createDOM(): HTMLElement {
    const span = document.createElement("span");
    span.className = "lexical-dimension-tag";
    span.setAttribute("data-dim-id", String(this.__dimId));
    return span;
  }

  updateDOM(): boolean {
    return false;
  }

  // JSON Serialization
  static importJSON(
    serializedNode: SerializedDimensionTagNode
  ): DimensionTagNode {
    return $createDimensionTagNode({
      dimId: serializedNode.dimId,
      label: serializedNode.label,
      group: serializedNode.group,
    });
  }

  exportJSON(): SerializedDimensionTagNode {
    return {
      type: "dimension-tag",
      dimId: this.__dimId,
      label: this.__label,
      group: this.__group,
      version: 1,
    };
  }

  // React Rendering
  decorate(): ReactNode {
    // Dimension tags are not rendered in LexicalEditor
    // They are handled by MarkdownText when displaying content
    // Return null to strip the tag from the editor
    return null;
  }

  // Behavior
  isInline(): boolean {
    return true;
  }

  isIsolated(): boolean {
    return true;
  }
}

// ============================================
// Factory Function
// ============================================

export function $createDimensionTagNode(payload: {
  dimId: number;
  label?: string;
  group?: string;
}): DimensionTagNode {
  return new DimensionTagNode(
    payload.dimId,
    payload.label || "",
    payload.group || ""
  );
}

// ============================================
// Type Guard
// ============================================

export function $isDimensionTagNode(
  node: LexicalNode | null | undefined
): node is DimensionTagNode {
  return node instanceof DimensionTagNode;
}

// ============================================
